// Supabase-backed data layer.
// Exposes an axios-like { get, post, put, delete } surface so existing
// consumers keep working unchanged. All data lives in Supabase Postgres;
// auth goes through Supabase Auth. There is no custom backend anymore.
import { supabase } from "./supabase";

// Build an axios-shaped error so formatApiError() keeps working.
function apiError(message) {
  const msg = message || "Something went wrong";
  const err = new Error(msg);
  err.response = { data: { detail: msg } };
  return err;
}

function ok(data) {
  return { data };
}

function splitPath(path) {
  return String(path).replace(/^\/+|\/+$/g, "").split("/");
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
async function authLogin({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error || !data?.user) throw apiError("Invalid email or password");
  return ok({
    user: { id: data.user.id, email: data.user.email, role: "admin" },
    access_token: data.session?.access_token,
  });
}

async function authLogout() {
  await supabase.auth.signOut();
  return ok({ ok: true });
}

async function authMe() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) throw apiError("Not authenticated");
  return ok({ id: data.user.id, email: data.user.email, role: "admin" });
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
async function listProjects() {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .order("order", { ascending: true });
  if (error) throw apiError(error.message);
  return ok(data || []);
}

async function getProject(id) {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw apiError(error.message);
  if (!data) throw apiError("Project not found");
  return ok(data);
}

async function listTimeline() {
  const { data, error } = await supabase
    .from("timeline_items")
    .select("*")
    .order("order", { ascending: false });
  if (error) throw apiError(error.message);
  return ok(data || []);
}

async function listPosts() {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("published", true)
    .order("date", { ascending: false });
  if (error) throw apiError(error.message);
  return ok(data || []);
}

async function listPostsAdmin() {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .order("date", { ascending: false });
  if (error) throw apiError(error.message);
  return ok(data || []);
}

async function getPost(slug) {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  if (error) throw apiError(error.message);
  if (!data) throw apiError("Post not found");
  return ok(data);
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
const TABLES = { projects: "projects", timeline: "timeline_items", posts: "posts" };

function dupSlugMessage(error) {
  if (error?.code === "23505") return "A post with this slug already exists";
  return error?.message || "Something went wrong";
}

async function insertRow(resource, body) {
  const table = TABLES[resource];
  const { data, error } = await supabase
    .from(table)
    .insert(body)
    .select()
    .single();
  if (error) throw apiError(dupSlugMessage(error));
  return ok(data);
}

async function updateRow(resource, id, body) {
  const table = TABLES[resource];
  const { data, error } = await supabase
    .from(table)
    .update(body)
    .eq("id", id)
    .select()
    .maybeSingle();
  if (error) throw apiError(dupSlugMessage(error));
  if (!data) throw apiError("Not found");
  return ok(data);
}

async function deleteRow(resource, id) {
  const table = TABLES[resource];
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw apiError(error.message);
  return ok({ ok: true });
}

// ---------------------------------------------------------------------------
// Router (axios-like surface)
// ---------------------------------------------------------------------------
const api = {
  async get(path) {
    const seg = splitPath(path);
    if (seg[0] === "auth" && seg[1] === "me") return authMe();
    if (seg[0] === "projects") return seg[1] ? getProject(seg[1]) : listProjects();
    if (seg[0] === "timeline") return listTimeline();
    if (seg[0] === "posts") {
      if (!seg[1]) return listPosts();
      if (seg[1] === "admin") return listPostsAdmin();
      return getPost(seg[1]);
    }
    throw apiError(`Unknown GET ${path}`);
  },

  async post(path, body) {
    const seg = splitPath(path);
    if (seg[0] === "auth" && seg[1] === "login") return authLogin(body || {});
    if (seg[0] === "auth" && seg[1] === "logout") return authLogout();
    if (TABLES[seg[0]] && !seg[1]) return insertRow(seg[0], body || {});
    throw apiError(`Unknown POST ${path}`);
  },

  async put(path, body) {
    const seg = splitPath(path);
    if (TABLES[seg[0]] && seg[1]) return updateRow(seg[0], seg[1], body || {});
    throw apiError(`Unknown PUT ${path}`);
  },

  async delete(path) {
    const seg = splitPath(path);
    if (TABLES[seg[0]] && seg[1]) return deleteRow(seg[0], seg[1]);
    throw apiError(`Unknown DELETE ${path}`);
  },
};

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (typeof detail === "object" && typeof detail.msg === "string")
    return detail.msg;
  return String(detail);
}
